import { useDfe } from '@/domains/jobs/hooks/useDfe';
import { useCreateDfe, useDfeMaterials } from '@/domains/jobs/hooks/useDfeMutations';
import { showSuccessToast } from '@/shared/notifications/toast-utils';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import type { CreateDfeBody } from '@skewb-works/contracts';
import {
  Calendar,
  FileImage,
  Paperclip,
  Plus,
  PoundSterling,
  Save,
  Trash2,
  Upload,
  User,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';

/**
 * Material option as presented to the UI. We key on synthetic_code because
 * that's the stable identifier in the rates catalogue and what the backend
 * expects back when creating a DFE line item.
 */
interface MaterialOption {
  value: string; // synthetic_code
  label: string;
  unit: string | null;
  unitCost: number;
}

interface DFELineItem {
  id: string;
  type: 'material' | 'custom';
  materialId?: string; // synthetic_code
  materialName?: string;
  materialUnit?: string | null;
  description?: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
}

interface DFESubmission {
  id: string;
  submittedDate: string;
  submittedBy: string;
  lineItems: DFELineItem[];
  notes?: string;
  images?: string[];
  totalAmount: number;
  status: 'pending' | 'approved' | 'rejected';
}

export function DFETab({ jobId }: { jobId: string }) {
  const numericJobId = Number(jobId);
  const { data: apiData, isLoading } = useDfe(numericJobId);
  const { data: materialsData, isLoading: materialsLoading } = useDfeMaterials();
  const createDfeMutation = useCreateDfe(numericJobId);
  const [showAddModal, setShowAddModal] = useState(false);

  const materialOptions = useMemo<MaterialOption[]>(() => {
    if (!materialsData?.data) return [];
    return materialsData.data
      .filter((m) => m.synthetic_code && m.name)
      .map((m) => ({
        value: m.synthetic_code!,
        label: m.name!,
        unit: m.unit,
        unitCost: Number(m.price ?? 0),
      }));
  }, [materialsData]);

  // For each line item, derive its quantity from total/price when the API
  // doesn't carry it explicitly (DB schema has no quantity column yet).
  const submissions = useMemo<DFESubmission[]>(() => {
    if (!apiData?.data) return [];
    return apiData.data.map((dfe) => ({
      id: `DFE-${String(dfe.id).padStart(3, '0')}`,
      submittedDate: dfe.created_at ?? '',
      submittedBy: dfe.created_by ?? 'Unknown',
      lineItems: dfe.line_items.map((item) => {
        const unitCost = Number(item.price ?? 0);
        const totalCost = Number(item.total ?? 0);
        const quantity = unitCost > 0 ? Math.max(1, Math.round(totalCost / unitCost)) : 1;
        return {
          id: String(item.id),
          type: 'material' as const,
          materialName: item.name ?? 'Unknown Material',
          materialUnit: item.unit,
          quantity,
          unitCost,
          totalCost,
        };
      }),
      notes: dfe.update_reason ?? undefined,
      totalAmount: Number(dfe.order_total_price ?? 0),
      status: (dfe.is_accept === true
        ? 'approved'
        : dfe.is_accept === false
          ? 'rejected'
          : (dfe.status ?? 'pending')) as 'pending' | 'approved' | 'rejected',
    }));
  }, [apiData]);

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E84A2B]" />
            <span className="ml-3 text-muted-foreground text-sm">Loading DFE data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-50 dark:bg-orange-950 flex items-center justify-center">
              <PoundSterling className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                Departure from Estimate (DFE)
              </h3>
              <p className="text-xs text-muted-foreground">
                Submit additional costs beyond original estimate
              </p>
            </div>
          </div>
          <Button
            className="bg-[#E84A2B] hover:bg-[#E84A2B]/90 text-white"
            onClick={() => setShowAddModal(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New DFE
          </Button>
        </div>
      </div>

      {/* DFE Submissions List */}
      <div className="space-y-4">
        {submissions.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <PoundSterling className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No DFE Submissions Yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Submit departure from estimate when additional costs arise beyond the original quote.
            </p>
            <Button variant="outline" onClick={() => setShowAddModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create First DFE
            </Button>
          </div>
        ) : (
          submissions.map((submission) => (
            <DFESubmissionCard key={submission.id} submission={submission} />
          ))
        )}
      </div>

      {/* Add DFE Modal */}
      <AddDFEModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        materialOptions={materialOptions}
        materialsLoading={materialsLoading}
        isSubmitting={createDfeMutation.isPending}
        onSubmit={async (payload) => {
          try {
            await createDfeMutation.mutateAsync(payload);
            showSuccessToast('DFE submitted');
            setShowAddModal(false);
          } catch {
            // Global mutation error handler shows the toast. Keep modal open.
          }
        }}
      />
    </div>
  );
}

function DFESubmissionCard({ submission }: { submission: DFESubmission }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <Badge className="bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200 border-green-600">
            Approved
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200 border-amber-600">
            Pending
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className="bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200 border-red-600">
            Rejected
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-semibold text-foreground">{submission.id}</span>
                {getStatusBadge(submission.status)}
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(submission.submittedDate).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </div>
                <div className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {submission.submittedBy}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-xs text-muted-foreground mb-1">Total Amount</div>
              <div className="text-lg font-bold text-foreground">
                \u00a3{submission.totalAmount.toFixed(2)}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? 'Hide' : 'View'} Details
            </Button>
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Line Items */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">Line Items</h4>
            <div className="space-y-2">
              {submission.lineItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border"
                >
                  <div className="flex-1">
                    <div className="text-sm font-medium text-foreground">
                      {item.type === 'material' ? item.materialName : item.description}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Quantity: {item.quantity} \u00d7 \u00a3{item.unitCost.toFixed(2)}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-foreground">
                    \u00a3{item.totalCost.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          {submission.notes && (
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">Notes</h4>
              <div className="p-3 rounded-lg bg-muted/50 border border-border text-sm text-foreground">
                {submission.notes}
              </div>
            </div>
          )}

          {/* Images */}
          {submission.images && submission.images.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">Supporting Evidence</h4>
              <div className="flex items-center gap-2 flex-wrap">
                {submission.images.map((image, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 p-2 rounded border border-border bg-muted/50 text-xs"
                  >
                    <FileImage className="w-4 h-4 text-muted-foreground" />
                    <span className="text-foreground">{image}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AddDFEModal({
  open,
  onOpenChange,
  onSubmit,
  materialOptions,
  materialsLoading,
  isSubmitting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: CreateDfeBody) => void | Promise<void>;
  materialOptions: MaterialOption[];
  materialsLoading: boolean;
  isSubmitting: boolean;
}) {
  const [lineItems, setLineItems] = useState<DFELineItem[]>([]);
  const [notes, setNotes] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [showLineItemForm, setShowLineItemForm] = useState(false);

  // Line item form state
  const [itemType, setItemType] = useState<'material' | 'custom'>('material');
  const [selectedMaterial, setSelectedMaterial] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [customCost, setCustomCost] = useState(0);

  const resetLineItemForm = () => {
    setItemType('material');
    setSelectedMaterial('');
    setCustomDescription('');
    setQuantity(1);
    setCustomCost(0);
  };

  const handleAddLineItem = (saveAndAddAnother = false) => {
    let newItem: DFELineItem;

    if (itemType === 'material') {
      const material = materialOptions.find((m) => m.value === selectedMaterial);
      if (!material) return;

      newItem = {
        id: `li-${Date.now()}`,
        type: 'material',
        materialId: material.value,
        materialName: material.label,
        materialUnit: material.unit,
        quantity,
        unitCost: material.unitCost,
        totalCost: material.unitCost * quantity,
      };
    } else {
      newItem = {
        id: `li-${Date.now()}`,
        type: 'custom',
        description: customDescription,
        quantity,
        unitCost: customCost,
        totalCost: customCost * quantity,
      };
    }

    setLineItems([...lineItems, newItem]);
    resetLineItemForm();

    if (!saveAndAddAnother) {
      setShowLineItemForm(false);
    }
  };

  const handleRemoveLineItem = (id: string) => {
    setLineItems(lineItems.filter((item) => item.id !== id));
  };

  const handleSubmit = async () => {
    if (lineItems.length === 0) {
      return;
    }

    const payload: CreateDfeBody = {
      line_items: lineItems.map((item) => ({
        name:
          item.type === 'material'
            ? (item.materialName ?? 'Material')
            : (item.description ?? 'Custom'),
        unit: item.type === 'material' ? (item.materialUnit ?? null) : null,
        price: item.unitCost,
        quantity: item.quantity,
        synthetic_code: item.type === 'material' ? (item.materialId ?? null) : null,
      })),
      notes: notes.trim() ? notes.trim() : null,
    };

    await onSubmit(payload);

    // Reset form — only reachable when the parent closed the modal (i.e. mutation
    // succeeded). If the mutation failed the modal stays open with state intact.
    setLineItems([]);
    setNotes('');
    setImages([]);
    setShowLineItemForm(false);
    resetLineItemForm();
  };

  const totalCost = lineItems.reduce((sum, item) => sum + item.totalCost, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Departure from Estimate</DialogTitle>
          <DialogDescription>
            Submit additional costs that exceed the original estimate for approval.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Line Items Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-foreground">Line Items</h4>
              {!showLineItemForm && (
                <Button variant="outline" size="sm" onClick={() => setShowLineItemForm(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Line Item
                </Button>
              )}
            </div>

            {/* Existing Line Items */}
            {lineItems.length > 0 && (
              <div className="space-y-2 mb-4">
                {lineItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border"
                  >
                    <div className="flex-1">
                      <div className="text-sm font-medium text-foreground">
                        {item.type === 'material' ? item.materialName : item.description}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Quantity: {item.quantity} \u00d7 \u00a3{item.unitCost.toFixed(2)}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-sm font-semibold text-foreground">
                        \u00a3{item.totalCost.toFixed(2)}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveLineItem(item.id)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Line Item Form */}
            {showLineItemForm && (
              <div className="p-4 rounded-lg border-2 border-[#E84A2B]/30 bg-muted/20 space-y-4">
                <div className="flex items-center gap-4">
                  <Button
                    variant={itemType === 'material' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setItemType('material')}
                    className={
                      itemType === 'material' ? 'bg-[#E84A2B] hover:bg-[#E84A2B]/90 text-white' : ''
                    }
                  >
                    Material
                  </Button>
                  <Button
                    variant={itemType === 'custom' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setItemType('custom')}
                    className={
                      itemType === 'custom' ? 'bg-[#E84A2B] hover:bg-[#E84A2B]/90 text-white' : ''
                    }
                  >
                    Custom
                  </Button>
                </div>

                {itemType === 'material' ? (
                  <>
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Material
                      </label>
                      <Select
                        value={selectedMaterial}
                        onValueChange={setSelectedMaterial}
                        disabled={materialsLoading || materialOptions.length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              materialsLoading
                                ? 'Loading materials…'
                                : materialOptions.length === 0
                                  ? 'No materials available'
                                  : 'Select material'
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {materialOptions.map((material) => (
                            <SelectItem key={material.value} value={material.value}>
                              {material.label} - \u00a3{material.unitCost.toFixed(2)}
                              {material.unit ? ` / ${material.unit}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Quantity
                      </label>
                      <Input
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                        min={1}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Description
                      </label>
                      <Input
                        value={customDescription}
                        onChange={(e) => setCustomDescription(e.target.value)}
                        placeholder="Describe the additional cost..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">
                          Quantity
                        </label>
                        <Input
                          type="number"
                          value={quantity}
                          onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                          min={1}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">
                          Unit Cost (\u00a3)
                        </label>
                        <Input
                          type="number"
                          value={customCost}
                          onChange={(e) => setCustomCost(Math.max(0, Number(e.target.value)))}
                          min={0}
                          step={0.01}
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowLineItemForm(false);
                      setItemType('material');
                      setSelectedMaterial('');
                      setCustomDescription('');
                      setQuantity(1);
                      setCustomCost(0);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddLineItem(true)}
                    disabled={
                      (itemType === 'material' && !selectedMaterial) ||
                      (itemType === 'custom' && (!customDescription || customCost <= 0))
                    }
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save & Add Another
                  </Button>
                  <Button
                    size="sm"
                    className="bg-[#E84A2B] hover:bg-[#E84A2B]/90 text-white"
                    onClick={() => handleAddLineItem(false)}
                    disabled={
                      (itemType === 'material' && !selectedMaterial) ||
                      (itemType === 'custom' && (!customDescription || customCost <= 0))
                    }
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Total Display */}
          {lineItems.length > 0 && (
            <div className="flex items-center justify-between p-4 rounded-lg bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-900">
              <span className="text-sm font-semibold text-foreground">Total DFE Amount</span>
              <span className="text-xl font-bold text-orange-600 dark:text-orange-400">
                \u00a3{totalCost.toFixed(2)}
              </span>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Notes (Optional)
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Provide additional context or justification for these costs..."
              rows={4}
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Supporting Evidence (Optional)
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground mb-2">Upload photos or documents</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setImages([...images, `evidence-${Date.now()}.jpg`])}
              >
                <Paperclip className="w-4 h-4 mr-2" />
                Browse Files
              </Button>
            </div>
            {images.length > 0 && (
              <div className="mt-3 space-y-2">
                {images.map((image, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-2 rounded border border-border bg-muted/50"
                  >
                    <div className="flex items-center gap-2 text-sm">
                      <FileImage className="w-4 h-4 text-muted-foreground" />
                      <span className="text-foreground">{image}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setImages(images.filter((_, i) => i !== idx))}
                      className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            className="bg-[#E84A2B] hover:bg-[#E84A2B]/90 text-white"
            onClick={handleSubmit}
            disabled={lineItems.length === 0 || isSubmitting}
          >
            {isSubmitting ? 'Submitting…' : 'Submit DFE'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
